import { Box } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

const END_OF_DAY = '23:59';

export const TimeSelect = ({
  value,
  property,
  onChange,
  disabled,
  allowIncompleteInput,
  endsBeforeMidnight,
}: {
  value: string;
  property: 'start' | 'end';
  onChange: (property: 'start' | 'end', value: string) => void;
  disabled?: boolean;
  allowIncompleteInput?: boolean;
  endsBeforeMidnight?: boolean;
}) => {
  const isEndBeforeMidnight = property === 'end' && endsBeforeMidnight;
  const changeTime = (time: string) =>
    onChange(property, isEndBeforeMidnight && time === '00:00' ? END_OF_DAY : time);

  const isPlaceholder = value === '--:--';
  const [hour, minute] = isPlaceholder ? [0, 0] : value.split(':').map((h) => parseInt(h));
  const isCompleteTime =
    Number.isInteger(hour) &&
    hour >= 0 &&
    hour <= 23 &&
    Number.isInteger(minute) &&
    minute >= 0 &&
    minute <= 59;
  // Always a real date: react-datepicker rebuilds the time off `selected`, and both a
  // null and a NaN-built one leave it holding an invalid date it throws on while rendering.
  const selected = isCompleteTime
    ? DateTime.fromObject({ hour, minute }).toJSDate()
    : DateTime.fromObject({ hour: 0, minute: 0 }).toJSDate();

  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<DatePicker>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        pickerRef.current?.setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePointerDown, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointerDown, true);
  }, [isOpen]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...(disabled && {
          '.react-datepicker__input-container': {
            border: '1px solid',
            borderColor: 'gray.300',
            background: 'gray.50',
            opacity: 0.5,
          },
        }),
        ...(property === 'end' &&
          !isEndBeforeMidnight && {
            // Put 00:00 last to indicate that it's the end of the day, not the start
            '.react-datepicker__time-list': {
              display: 'flex',
              flexDirection: 'column',
            },
            '.react-datepicker__time-list li:first-of-type': {
              order: 999,
            },
          }),
      }}
    >
      <DatePicker
        ref={pickerRef}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        selected={selected}
        onChange={(date, e: BaseSyntheticEvent | undefined) => {
          if (e) {
            // Type
            changeTime(e?.target?.value);
          } else if (date) {
            // Click
            changeTime(DateTime.fromJSDate(date).toFormat('HH:mm'));
          }
        }}
        {...(allowIncompleteInput && {
          onChangeRaw: (event) => {
            if (typeof event.target.value !== 'string') return;
            changeTime(event.target.value);
            event.preventDefault();
          },
        })}
        showTimeSelect
        showTimeSelectOnly
        timeCaption={''}
        timeIntervals={15}
        {...(isEndBeforeMidnight && {
          excludeTimes: [DateTime.fromObject({ hour: 0, minute: 0 }).toJSDate()],
          injectTimes: [DateTime.fromObject({ hour: 23, minute: 59 }).toJSDate()],
        })}
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        disabled={disabled}
        value={isPlaceholder ? '--:--' : allowIncompleteInput ? value : undefined}
      />
    </Box>
  );
};
